import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from '../utils/toast';
import PasteUploadArea from './PasteUploadArea';

// Simplified MasterDataSelector for CustomPageSubmissionEdit
const SimpleMasterDataSelector = ({ value, onChange, masterDataList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredData = masterDataList.filter(item => 
    item.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.clientAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (item) => {
    onChange(item);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a client..."}
        value={value ? value.clientName : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredData.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {masterDataList.length === 0 ? 'No master data available' : 'No clients found'}
            </div>
          ) : (
            <div className="py-1">
              {filteredData.map((item) => (
                <button
                  key={item._id}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{item.clientName}</div>
                  {item.clientAddress && (
                    <div className="text-sm text-gray-600">{item.clientAddress}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

// Simplified UserSelector for CustomPageSubmissionEdit
const SimpleUserSelector = ({ value, onChange, userList, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredUsers = userList.filter(user => 
    user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (user) => {
    onChange(user);
    setShowDropdown(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <input
        type="text"
        placeholder={placeholder || "Search for a user..."}
        value={value ? `${value.firstName} ${value.lastName}` : searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setShowDropdown(true)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      
      {showDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">No users found</div>
          ) : (
            <div className="py-1">
              {filteredUsers.map((user) => (
                <button
                  key={user._id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                >
                  <div className="font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                  <div className="text-sm text-gray-600">{user.email}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

const CustomPageSubmissionEdit = () => {
  const { submissionId } = useParams();
  const { user, checkPermission } = useAuth();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [customPage, setCustomPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [submissionName, setSubmissionName] = useState('');
  const [masterDataList, setMasterDataList] = useState([]);
  const [userList, setUserList] = useState([]);
  const [documentVerifications, setDocumentVerifications] = useState([]);
  const [documentUploads, setDocumentUploads] = useState({});

  const canUpdate = checkPermission('Update Custom Page Submission');

  useEffect(() => {
    fetchSubmission();
    fetchMasterData();
    fetchUsers();
  }, [submissionId]);

  const fetchSubmission = async () => {
    try {
      const response = await api.get(`/custom-page-submissions/${submissionId}`);
      if (response.data.success) {
        setSubmission(response.data.data);
        setCustomPage(response.data.data.customPage);
        setSubmissionName(response.data.data.name);
        
        // Initialize form data from submission
        const initialData = {};
        if (response.data.data.data) {
          Object.keys(response.data.data.data).forEach(key => {
            initialData[key] = response.data.data.data.get(key);
          });
        }
        setFormData(initialData);
        
        // Initialize document uploads from existing Checklist
        const initialUploads = {};
        if (response.data.data.documentVerifications) {
          response.data.data.documentVerifications.forEach(docVer => {
            initialUploads[docVer.documentId] = docVer.files || [];
          });
        }
        setDocumentUploads(initialUploads);
        
        // Debug log for Checklist
        console.log('Checklist in Edit View:', response.data.data.documentVerifications);
        
        // Fetch Checklist for the custom page
        await fetchDocumentVerifications();
      }
    } catch (error) {
      console.error('Error fetching submission:', error);
      toast.error('Failed to fetch submission');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentVerifications = async () => {
    try {
      const response = await api.get('/custom-documents');
      if (response.data.success) {
        const docs = response.data.data.docs || response.data.data || [];
        setDocumentVerifications(docs);
        
        // If we don't have existing document uploads, pre-populate from custom documents
        if (Object.keys(documentUploads).length === 0) {
          const requiredDocs = getRequiredDocumentVerifications();
          const initialUploads = {};
          for (const doc of requiredDocs) {
            if (doc.file) {
              // Convert the uploaded document to the format expected by PasteUploadArea
              initialUploads[doc._id] = [{
                fileName: doc.file.name,
                fileUrl: doc.file.url,
                originalName: doc.file.originalName,
                mimeType: doc.file.mimeType,
                size: doc.file.size
              }];
            }
          }
          setDocumentUploads(prev => ({ ...prev, ...initialUploads }));
        }
      }
    } catch (error) {
      console.error('Error fetching Checklist:', error);
    }
  };

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/master-data');
      setMasterDataList(response.data.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      if (response.data.success) {
        setUserList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleDocumentUpload = (documentId, files) => {
    setDocumentUploads(prev => ({
      ...prev,
      [documentId]: files
    }));
  };

  const uploadFileToServer = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        return {
          fileName: file.name,
          fileUrl: response.data.url,
          originalName: file.name,
          mimeType: file.type,
          size: file.size
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleFileUpload = async (documentId, files) => {
    try {
      // Upload each file to the server
      const uploadedFiles = [];
      for (const file of files) {
        // If the file is already a File object (new upload), upload it
        if (file instanceof File) {
          const uploadedFile = await uploadFileToServer(file);
          if (uploadedFile) {
            uploadedFiles.push(uploadedFile);
          }
        } else {
          // If it's already an uploaded file object, keep it as is
          uploadedFiles.push(file);
        }
      }
      
      // Update the document uploads with the uploaded files
      if (uploadedFiles.length > 0) {
        handleDocumentUpload(documentId, uploadedFiles);
      }
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.error('Failed to upload files');
    }
  };

  const getRequiredDocumentVerifications = () => {
    if (!customPage || !customPage.documentVerifications || !documentVerifications.length) {
      return [];
    }
    
    return documentVerifications.filter(doc => 
      customPage.documentVerifications.some(requiredDoc => 
        requiredDoc === doc._id || requiredDoc._id === doc._id
      )
    );
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!submissionName.trim()) {
      newErrors.name = 'Submission name is required';
    }
    
    if (customPage) {
      const requiredFields = customPage.fields.filter(f => f.required);
      requiredFields.forEach(field => {
        if (!formData[field.name] || formData[field.name] === '') {
          newErrors[field.name] = `${field.label} is required`;
        }
      });
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: submissionName.trim(),
        data: formData
      };

      // Add Checklist if any are required
      const requiredDocs = getRequiredDocumentVerifications();
      if (requiredDocs.length > 0) {
        updateData.documentVerifications = requiredDocs.map(doc => {
          const uploadedFiles = documentUploads[doc._id] || [];
          
          // If files were uploaded, use the uploaded file URL
          // Otherwise, use the custom document's file URL as default
          const fileUrl = uploadedFiles.length > 0 
            ? uploadedFiles[0].fileUrl 
            : (doc.file ? doc.file.url : null);
          
          const fileName = uploadedFiles.length > 0 
            ? uploadedFiles[0].fileName 
            : (doc.file ? doc.file.name : doc.name);
          
          return {
            documentId: doc._id,
            documentName: doc.name,
            fileUrl: fileUrl,
            fileName: fileName,
            files: uploadedFiles.length > 0 ? uploadedFiles : []
          };
        });
      }

      const response = await api.put(`/custom-page-submissions/${submissionId}`, updateData);
      
      if (response.data.success) {
        toast.success('Submission updated successfully');
        navigate(`/admin/settings/custom-page-submissions/${customPage._id}`);
      }
    } catch (error) {
      console.error('Error updating submission:', error);
      toast.error(error.response?.data?.message || 'Failed to update submission');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name];
    const error = errors[field.name];

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, parseFloat(e.target.value) || '')}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select an option</option>
            {(field.options || []).map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multitext':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );

      case 'upload':
        return (
          <PasteUploadArea
            files={value || []}
            onFilesChange={(files) => handleInputChange(field.name, files)}
            multiple={true}
            accept="*/*"
          />
        );

      case 'masterdata':
        return (
          <SimpleMasterDataSelector
            value={value}
            onChange={(masterData) => handleInputChange(field.name, masterData)}
            masterDataList={masterDataList}
            placeholder={field.placeholder}
          />
        );

      case 'user':
        return (
          <SimpleUserSelector
            value={value}
            onChange={(user) => handleInputChange(field.name, user)}
            userList={userList}
            placeholder={field.placeholder}
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Submission Not Found</h2>
        <p className="text-gray-600">The requested submission could not be found.</p>
      </div>
    );
  }

  if (!canUpdate) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to edit this submission.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Submission</h1>
            <p className="text-gray-600">
              {customPage?.name} - {submission.name}
            </p>
          </div>
          <button
            onClick={() => navigate(`/admin/settings/custom-page-submissions/${customPage?._id}`)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Back to Submissions
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Submission Name Field */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Submission Name *
          </label>
          <input
            type="text"
            value={submissionName}
            onChange={(e) => setSubmissionName(e.target.value)}
            placeholder="Enter a name for this submission..."
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Form Fields */}
        {customPage?.fields.map((field, index) => (
          <div key={index} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {renderField(field)}
            
            {errors[field.name] && (
              <p className="text-sm text-red-600">{errors[field.name]}</p>
            )}
            
            {field.type === 'date' && field.isDeadlineField && (
              <p className="text-sm text-blue-600">
                This field will be used to calculate task deadlines
              </p>
            )}
          </div>
        ))}

        {/* Checklist Section */}
        {getRequiredDocumentVerifications().length > 0 && (
          <div className="space-y-4 pt-6 border-t">
            <h3 className="text-lg font-medium text-gray-900">Required Checklist</h3>
            <p className="text-sm text-gray-600">
              The following documents are required for this submission. If no file is uploaded, the system will use the default file from the custom document:
            </p>
            
            <div className="space-y-4">
              {getRequiredDocumentVerifications().map((doc) => (
                <div key={doc._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{doc.name}</h4>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Required
                      </span>
                      {(documentUploads[doc._id] && documentUploads[doc._id].length > 0) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Custom Upload
                        </span>
                      )}
                      {(!documentUploads[doc._id] || documentUploads[doc._id].length === 0) && doc.file && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Using Default
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <PasteUploadArea
                    files={documentUploads[doc._id] || []}
                    onFilesChange={(files) => handleFileUpload(doc._id, files)}
                    multiple={true}
                    accept="*/*"
                  />
                  
                  {doc.category && (
                    <p className="text-xs text-gray-500 mt-2">
                      Category: {doc.category}
                    </p>
                  )}
                  
                  {doc.file && !submission?.documentVerifications?.some(dv => dv.documentId === doc._id) && (
                    <p className="text-xs text-blue-600 mt-2">
                      ✓ Default file available: {doc.file.originalName || doc.file.name}
                      {(!documentUploads[doc._id] || documentUploads[doc._id].length === 0) && 
                        " (will be used if no file is uploaded)"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={() => navigate(`/admin/settings/custom-page-submissions/${customPage?._id}`)}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomPageSubmissionEdit; 